import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort, SortDirection } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { MxEventName } from '@axon-enterprise/track';
import { SnAbilityService } from '@lib/ability';
import { ISnListQueryParameters, ISnListResponse, SortParameters } from '@lib/api';
import { SnContentType } from '@lib/api/entities';
import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth';
import { snZoomIn, snZoomOut } from '@lib/common/animations';
import { MixpanelService } from '@lib/mixpanel';
import { DolpShareComponent, FsDolpShareService } from '@lib/share';
import { t } from '@lib/translation';
import { FsLiveUpdateSwitchService } from '@lib/transport/live-update-switch';
import { SnActionConfig } from '@lib/ui/actions';
import { SnDataListComponent } from '@lib/ui/data-list';
import { SnLayoutService } from '@lib/ui/layout';

import { LprHotListAPI, SnLpr } from '../../api';
import { LprHotlistsPlatesDialogComponent } from '../lpr-hotlists-plates-dialog/lpr-hotlists-plates-dialog.component';
import { ManageInternalHotlistDialogComponent } from '../manage-internal-hotlist-dialog/manage-internal-hotlist-dialog.component';

@Component({
  selector: 'fs-lpr-hotlists-list',
  templateUrl: './lpr-hotlists-list.component.html',
  styleUrls: ['./lpr-hotlists-list.component.scss'],
  animations: [snZoomIn, snZoomOut],
})
export class LprHotlistsListComponent implements OnDestroy, OnInit {
  @ViewChild('listElement', { static: true })
  public readonly list: SnDataListComponent<SnLpr>;
  public readonly loadData = this._loadData.bind(this);

  public readonly columns = this._getColumns();
  public readonly actions: SnActionConfig<SnLpr>[] = this._getActions();

  public sort = 'name';
  public sortDirection: SortDirection = 'asc';

  public canAddHotlist: boolean;
  public canDeleteHotlist: boolean;
  public updateHotlistAvailable: boolean;
  public startTime = new Date();

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _lprHotlistAPI: LprHotListAPI,
    private readonly _layout: SnLayoutService,
    private readonly _dialog: MatDialog,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _currentUser: SnCurrentUser,
    private readonly _dolpService: FsDolpShareService,
    private readonly _liveUpdateSwitchService: FsLiveUpdateSwitchService,
    private readonly ability: SnAbilityService,
    private readonly _mixpanelService: MixpanelService,
  ) {}

  public ngOnInit(): void {
    this._layout.appbar.actions.set([
      {
        label: t('common.refresh'),
        icon: {
          fontIcon: 'fa-redo-alt',
          fontSet: 'fal',
        },
        click: this.updateHotlistData.bind(this),
      },
    ]);
    this._initSearchHandling();
    this.canAddHotlist = this.ability.can({
      subject: 'lpr/lprhotlist',
      action: 'add',
    });
    this.canDeleteHotlist = this.ability.can({
      subject: 'lpr/lprhotlist',
      action: 'delete',
    });
    setTimeout(() => {
      this._initHotlistsUpdates();
    }, 1500);
  }

  public ngOnDestroy(): void {
    this._mixpanelService.logSessionDuration(this.startTime, 'LPR Hotlists');
    this._liveUpdateSwitchService.unsubscribeFromChannel(
      `${this._currentOrganization.id}:dolp_hotlist`,
    );
    this._layout.appbar.actions.clear();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public addInternalHotlist(): void {
    this._dialog
      .open(ManageInternalHotlistDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((data) => {
        if (!data) {
          return;
        }
        this._createHotlist(data);
      });
  }

  public search(value: string): void {
    this._updateQueryParams({ search: value });
  }

  public onSortChange(sort: Sort): void {
    this.sort = sort.active;
    this.sortDirection = sort.direction;

    this.updateHotlistData();
  }

  public updateHotlistData(): void {
    this._reloadData();
    this.updateHotlistAvailable = false;
  }

  private _initHotlistsUpdates(): void {
    const org = this._currentOrganization;
    this._liveUpdateSwitchService.subscribeToChannel({
      channel: `${org.id}:dolp_hotlist`,
      callback: ({ name, data }) => {
        if (data || name) {
          this.updateHotlistAvailable = true;
        }
      },
    });
  }

  private _createHotlist(data: any): void {
    this._lprHotlistAPI
      .createHotlist({
        name: data.name,
        organization: this._currentOrganization.id,
        deduplicationSources: data.deduplicationSources,
        properties: {
          fields: data.fields,
          reasonOptions: data.reasonOptions,
          disableDuplicates: data.disableDuplicates,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._mixpanelService.trackMetric(MxEventName.CreateListHotAf, {});
        this.updateHotlistData();
      });
  }

  private _updateHotlist(id: string, data: any): void {
    this._lprHotlistAPI
      .updateHotlist(id, {
        name: data.name,
        deduplicationSources: data.deduplicationSources,
        defaultExpirationDuration: data.defaultExpirationDuration,
        properties: {
          fields: data.fields,
          reasonOptions: data.reasonOptions,
          disableDuplicates: data.disableDuplicates,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((lpr) => {
        this.list.dataSource.updateItem(lpr);
      });
  }

  private _deleteHotlist(id: string): void {
    this._lprHotlistAPI
      .deleteHotlist(id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._reloadData();
      });
  }

  private _getColumns(): string[] {
    return ['hotlist', 'provider', 'filterAlerts', 'actions'];
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<SnLpr>> {
    return this._lprHotlistAPI.getLprHotlists(this._prepareLoadQueryParameters(params));
  }

  private _prepareLoadQueryParameters(params: ISnListQueryParameters): ISnListQueryParameters {
    return {
      type: 'Internal',
      ordering: new SortParameters({
        active: this.sort,
        direction: this.sortDirection,
      }).convertedActive,
      ...params,
    };
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _initSearchHandling(): void {
    this._layout.appbar.search.value = this._activatedRoute.snapshot.queryParams.search;
    this._layout.appbar.search.change$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.search(value);
    });
  }

  private _updateQueryParams(params: Params): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: params,
    });
  }

  private _getActions(): SnActionConfig<SnLpr>[] {
    return [
      {
        label: t('common.users'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-user',
        },
        hidden: (item) =>
          !item.dolpUsers?.admin?.includes(this._currentUser.id) && !this.canAddHotlist,
        click: (item) => {
          this._mixpanelService.trackMetric(MxEventName.UserMgmtHotAf, {});
          const dialogRef = this._dialog.open(DolpShareComponent, {
            data: {
              shareTitle: t('vehicleIntelligence.hotlist.shareHotlist'),
              loadType: SnContentType.Hotlists,
              inputItems: item,
            },
            width: '1080px',
          });
          dialogRef
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe((res) => {
              if (res) {
                this._dolpService
                  .addLprHotlistDolp(item.id, res)
                  .pipe(takeUntil(this._destroy$))
                  .subscribe();
              }
            });
        },
      },
      {
        label: t('vehicleIntelligence.hotlist.viewPlates'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-table-list',
        },
        click: (item) => {
          this._mixpanelService.trackMetric(MxEventName.ViewPlatesHotAf, {});
          this._dialog.open(LprHotlistsPlatesDialogComponent, {
            data: {
              hotlists: item,
              hotlist: item,
            },
            width: '1080px',
          });
        },
      },
      {
        label: t('common.edit'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-pen',
        },
        hidden: (item) =>
          !(
            (item.type === 'Internal' && item.dolpUsers?.admin?.includes(this._currentUser.id)) ||
            item.dolpUsers?.editor?.includes(this._currentUser.id) ||
            this.canAddHotlist
          ),
        click: (item) => {
          this._dialog
            .open(ManageInternalHotlistDialogComponent, {
              data: {
                item,
                canDelete: this.canDeleteHotlist,
              },
            })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe((data) => {
              if (data === 'delete') {
                this._deleteHotlist(item.id);
              } else if (data) {
                this._updateHotlist(item.id, data);
                this._mixpanelService.trackMetric(MxEventName.EditListHotAf, {});
              }
            });
        },
      },
      {
        label: t('common.delete'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-trash-can',
        },
        hidden: (item) =>
          !(
            item.type === 'Internal' &&
            (item.dolpUsers?.admin?.includes(this._currentUser.id) || this.canDeleteHotlist)
          ),
        click: (item) => {
          this._mixpanelService.trackMetric(MxEventName.DeleteListHotAf, {});
          this._deleteHotlist(item.id);
        },
      },
    ];
  }
}
